import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface UserProfileData {
  id: string
  email: string
  firstName: string
  lastName: string
  role: string
  department: string
  phone: string
  avatar: string | null
  bio: string
  jobTitle: string
  company: string
  location: string
  createdAt: string
  updatedAt: string
  lastLogin: string
  twoFactorEnabled: boolean
  verified: boolean
}

interface PreferenceCategory {
  id: string
  name: string
  icon: string
}

interface ActivityStats {
  totalQueries: number
  totalReports: number
  totalExports: number
  marketsViewed: number
  avgSessionDuration: string
  loginCount: number
  topMarkets: string[]
  topFeatures: string[]
}

interface Session {
  id: string
  device: string
  ip: string
  location: string
  lastActive: string
  current: boolean
}

interface ApiKey {
  id: string
  name: string
  keyPreview: string
  createdAt: string
  lastUsed: string
  permissions: string[]
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://192.168.0.98:8090/api'

export default function UserProfile() {
  const [activeTab, setActiveTab] = useState<'profile' | 'preferences' | 'security' | 'activity'>('profile')
  const [profile, setProfile] = useState<UserProfileData | null>(null)
  const [preferences, setPreferences] = useState<Record<string, Record<string, unknown>>>({})
  const [preferenceCategories, setPreferenceCategories] = useState<PreferenceCategory[]>([])
  const [activityStats, setActivityStats] = useState<ActivityStats | null>(null)
  const [sessions, setSessions] = useState<Session[]>([])
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activePreferenceCategory, setActivePreferenceCategory] = useState('display')

  // Edit states
  const [editingProfile, setEditingProfile] = useState(false)
  const [profileForm, setProfileForm] = useState<Partial<UserProfileData>>({})

  // 2FA states
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false)
  const [twoFactorData, setTwoFactorData] = useState<{ secret: string; qrCode: string; backupCodes: string[] } | null>(null)
  const [twoFactorCode, setTwoFactorCode] = useState('')

  // API key states
  const [showNewApiKey, setShowNewApiKey] = useState(false)
  const [newApiKeyName, setNewApiKeyName] = useState('')
  const [newApiKeyPermissions, setNewApiKeyPermissions] = useState<string[]>(['read'])
  const [createdApiKey, setCreatedApiKey] = useState<string | null>(null)

  // Get current user from localStorage
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const userId = currentUser.email || 'john.doe@expandly.com'

  useEffect(() => {
    fetchProfile()
    fetchPreferenceCategories()
    fetchPreferences()
    fetchActivityStats()
    fetchSessions()
    fetchApiKeys()
  }, [])

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/profile/${userId}`)
      if (response.ok) {
        const data = await response.json()
        setProfile(data)
        setProfileForm(data)
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchPreferenceCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/preference-categories`)
      if (response.ok) {
        const data = await response.json()
        setPreferenceCategories(data)
      }
    } catch (error) {
      console.error('Failed to fetch preference categories:', error)
    }
  }

  const fetchPreferences = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/preferences/${userId}`)
      if (response.ok) {
        const data = await response.json()
        setPreferences(data)
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error)
    }
  }

  const fetchActivityStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/activity-stats/${userId}`)
      if (response.ok) {
        const data = await response.json()
        setActivityStats(data)
      }
    } catch (error) {
      console.error('Failed to fetch activity stats:', error)
    }
  }

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/sessions/${userId}`)
      if (response.ok) {
        const data = await response.json()
        setSessions(data)
      }
    } catch (error) {
      console.error('Failed to fetch sessions:', error)
    }
  }

  const fetchApiKeys = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/api-keys/${userId}`)
      if (response.ok) {
        const data = await response.json()
        setApiKeys(data)
      }
    } catch (error) {
      console.error('Failed to fetch API keys:', error)
    }
  }

  const saveProfile = async () => {
    setSaving(true)
    try {
      const response = await fetch(`${API_BASE}/user-profile/profile/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: profileForm.firstName,
          last_name: profileForm.lastName,
          phone: profileForm.phone,
          bio: profileForm.bio,
          job_title: profileForm.jobTitle,
          company: profileForm.company,
          location: profileForm.location,
        }),
      })
      if (response.ok) {
        const data = await response.json()
        setProfile(data)
        setEditingProfile(false)
      }
    } catch (error) {
      console.error('Failed to save profile:', error)
    } finally {
      setSaving(false)
    }
  }

  const savePreference = async (category: string, key: string, value: unknown) => {
    try {
      const updatedCategory = {
        ...preferences[category],
        [key]: value,
      }
      const response = await fetch(`${API_BASE}/user-profile/preferences/${userId}/${category}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedCategory),
      })
      if (response.ok) {
        setPreferences(prev => ({
          ...prev,
          [category]: updatedCategory,
        }))
      }
    } catch (error) {
      console.error('Failed to save preference:', error)
    }
  }

  const enableTwoFactor = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/two-factor/${userId}/enable`, {
        method: 'POST',
      })
      if (response.ok) {
        const data = await response.json()
        setTwoFactorData(data)
        setShowTwoFactorSetup(true)
      }
    } catch (error) {
      console.error('Failed to enable 2FA:', error)
    }
  }

  const verifyTwoFactor = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/two-factor/${userId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: twoFactorCode }),
      })
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setProfile(prev => prev ? { ...prev, twoFactorEnabled: true } : null)
          setShowTwoFactorSetup(false)
          setTwoFactorCode('')
        }
      }
    } catch (error) {
      console.error('Failed to verify 2FA:', error)
    }
  }

  const disableTwoFactor = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/two-factor/${userId}/disable`, {
        method: 'POST',
      })
      if (response.ok) {
        setProfile(prev => prev ? { ...prev, twoFactorEnabled: false } : null)
      }
    } catch (error) {
      console.error('Failed to disable 2FA:', error)
    }
  }

  const revokeSession = async (sessionId: string) => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/sessions/${userId}/${sessionId}`, {
        method: 'DELETE',
      })
      if (response.ok) {
        setSessions(prev => prev.filter(s => s.id !== sessionId))
      }
    } catch (error) {
      console.error('Failed to revoke session:', error)
    }
  }

  const createApiKey = async () => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/api-keys/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newApiKeyName,
          permissions: newApiKeyPermissions,
        }),
      })
      if (response.ok) {
        const data = await response.json()
        setCreatedApiKey(data.key)
        setApiKeys(prev => [...prev, { ...data, key: undefined }])
        setNewApiKeyName('')
        setNewApiKeyPermissions(['read'])
      }
    } catch (error) {
      console.error('Failed to create API key:', error)
    }
  }

  const deleteApiKey = async (keyId: string) => {
    try {
      const response = await fetch(`${API_BASE}/user-profile/api-keys/${userId}/${keyId}`, {
        method: 'DELETE',
      })
      if (response.ok) {
        setApiKeys(prev => prev.filter(k => k.id !== keyId))
      }
    } catch (error) {
      console.error('Failed to delete API key:', error)
    }
  }

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase() || '??'
  }

  const renderPreferenceInput = (category: string, key: string, value: unknown, type: 'boolean' | 'string' | 'number' | 'select', options?: string[]) => {
    if (type === 'boolean') {
      return (
        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={value as boolean}
            onChange={(e) => savePreference(category, key, e.target.checked)}
            style={{ width: '18px', height: '18px', accentColor: '#3b82f6' }}
          />
          <span style={{ color: '#374151' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
        </label>
      )
    }
    if (type === 'select' && options) {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#374151' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
          <select
            value={value as string}
            onChange={(e) => savePreference(category, key, e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              fontSize: '14px',
              minWidth: '150px',
            }}
          >
            {options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )
    }
    if (type === 'string') {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#374151' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
          <input
            type="text"
            value={value as string}
            onChange={(e) => savePreference(category, key, e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              fontSize: '14px',
              width: '150px',
            }}
          />
        </div>
      )
    }
    if (type === 'number') {
      return (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#374151' }}>{key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</span>
          <input
            type="number"
            value={value as number}
            onChange={(e) => savePreference(category, key, parseInt(e.target.value))}
            style={{
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              fontSize: '14px',
              width: '100px',
            }}
          />
        </div>
      )
    }
    return null
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
        <Header />
        <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
          Loading profile...
        </div>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
            User Profile & Preferences
          </h1>
          <p style={{ color: '#64748b', fontSize: '14px' }}>
            Manage your account settings, preferences, and security options
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '4px',
          marginBottom: '24px',
          background: '#f1f5f9',
          padding: '4px',
          borderRadius: '10px',
          width: 'fit-content'
        }}>
          {[
            { id: 'profile', label: 'Profile', icon: '👤' },
            { id: 'preferences', label: 'Preferences', icon: '⚙️' },
            { id: 'security', label: 'Security', icon: '🔐' },
            { id: 'activity', label: 'Activity', icon: '📊' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              style={{
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                background: activeTab === tab.id ? 'white' : 'transparent',
                color: activeTab === tab.id ? '#1e293b' : '#64748b',
                fontWeight: activeTab === tab.id ? '600' : '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && profile && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
            {/* Avatar Card */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px',
              textAlign: 'center'
            }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                color: 'white',
                fontSize: '40px',
                fontWeight: '700'
              }}>
                {profile.avatar ? (
                  <img src={profile.avatar} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                ) : (
                  getInitials(profile.firstName, profile.lastName)
                )}
              </div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                {profile.firstName} {profile.lastName}
              </h3>
              <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '8px' }}>{profile.jobTitle}</p>
              <span style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '20px',
                background: '#dbeafe',
                color: '#1d4ed8',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'capitalize'
              }}>
                {profile.role}
              </span>
              <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e2e8f0' }}>
                <p style={{ fontSize: '13px', color: '#64748b' }}>
                  Member since {new Date(profile.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Profile Details Card */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '16px 20px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b' }}>Profile Information</h3>
                {!editingProfile ? (
                  <button
                    onClick={() => setEditingProfile(true)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: 'white',
                      color: '#374151',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Edit Profile
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        setEditingProfile(false)
                        setProfileForm(profile)
                      }}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        background: 'white',
                        color: '#374151',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveProfile}
                      disabled={saving}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#3b82f6',
                        color: 'white',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                )}
              </div>

              <div style={{ padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  {[
                    { label: 'First Name', key: 'firstName' },
                    { label: 'Last Name', key: 'lastName' },
                    { label: 'Email', key: 'email', readonly: true },
                    { label: 'Phone', key: 'phone' },
                    { label: 'Job Title', key: 'jobTitle' },
                    { label: 'Company', key: 'company' },
                    { label: 'Location', key: 'location' },
                    { label: 'Department', key: 'department', readonly: true },
                  ].map(field => (
                    <div key={field.key}>
                      <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                        {field.label}
                      </label>
                      {editingProfile && !field.readonly ? (
                        <input
                          type="text"
                          value={(profileForm as Record<string, string>)[field.key] || ''}
                          onChange={(e) => setProfileForm(prev => ({ ...prev, [field.key]: e.target.value }))}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            fontSize: '14px'
                          }}
                        />
                      ) : (
                        <p style={{ fontSize: '14px', color: '#1e293b', fontWeight: '500' }}>
                          {(profile as unknown as Record<string, string>)[field.key] || '-'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                    Bio
                  </label>
                  {editingProfile ? (
                    <textarea
                      value={profileForm.bio || ''}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        resize: 'vertical'
                      }}
                    />
                  ) : (
                    <p style={{ fontSize: '14px', color: '#1e293b' }}>
                      {profile.bio || '-'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '24px' }}>
            {/* Category Sidebar */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '12px',
              height: 'fit-content'
            }}>
              {preferenceCategories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActivePreferenceCategory(cat.id)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: activePreferenceCategory === cat.id ? '#f1f5f9' : 'transparent',
                    color: activePreferenceCategory === cat.id ? '#1e293b' : '#64748b',
                    fontWeight: activePreferenceCategory === cat.id ? '600' : '500',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '14px',
                    marginBottom: '4px',
                    transition: 'all 0.2s'
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Preference Settings */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '20px' }}>
                {preferenceCategories.find(c => c.id === activePreferenceCategory)?.name}
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {preferences[activePreferenceCategory] && Object.entries(preferences[activePreferenceCategory]).map(([key, value]) => {
                  if (key === 'userId' || key === 'updatedAt') return null

                  let type: 'boolean' | 'string' | 'number' | 'select' = 'string'
                  let options: string[] | undefined

                  if (typeof value === 'boolean') type = 'boolean'
                  else if (typeof value === 'number') type = 'number'
                  else if (key === 'theme') { type = 'select'; options = ['light', 'dark', 'system'] }
                  else if (key === 'language') { type = 'select'; options = ['en', 'de', 'fr', 'es', 'it'] }
                  else if (key === 'defaultFormat') { type = 'select'; options = ['pdf', 'csv', 'excel', 'json'] }
                  else if (key === 'scheduleFrequency') { type = 'select'; options = ['daily', 'weekly', 'monthly'] }
                  else if (key === 'scheduleDay') { type = 'select'; options = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] }
                  else if (key === 'queryConfidence') { type = 'select'; options = ['low', 'medium', 'high'] }
                  else if (key === 'responseDetail') { type = 'select'; options = ['brief', 'detailed', 'comprehensive'] }

                  return (
                    <div key={key} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                      {renderPreferenceInput(activePreferenceCategory, key, value, type, options)}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Two-Factor Authentication */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                    Two-Factor Authentication
                  </h3>
                  <p style={{ color: '#64748b', fontSize: '14px' }}>
                    Add an extra layer of security to your account
                  </p>
                </div>
                {profile?.twoFactorEnabled ? (
                  <button
                    onClick={disableTwoFactor}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: '1px solid #ef4444',
                      background: 'white',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Disable 2FA
                  </button>
                ) : (
                  <button
                    onClick={enableTwoFactor}
                    style={{
                      padding: '10px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      background: '#10b981',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Enable 2FA
                  </button>
                )}
              </div>

              {profile?.twoFactorEnabled && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 16px',
                  background: '#dcfce7',
                  borderRadius: '8px',
                  color: '#166534'
                }}>
                  <span style={{ fontSize: '18px' }}>✓</span>
                  Two-factor authentication is enabled
                </div>
              )}

              {showTwoFactorSetup && twoFactorData && (
                <div style={{ marginTop: '20px', padding: '20px', background: '#f8fafc', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '16px', color: '#1e293b' }}>Setup Two-Factor Authentication</h4>
                  <p style={{ marginBottom: '16px', color: '#64748b', fontSize: '14px' }}>
                    Scan this QR code with your authenticator app, then enter the code below:
                  </p>
                  <div style={{
                    background: 'white',
                    padding: '16px',
                    borderRadius: '8px',
                    marginBottom: '16px',
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    wordBreak: 'break-all'
                  }}>
                    Secret: {twoFactorData.secret}
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                    <input
                      type="text"
                      value={twoFactorCode}
                      onChange={(e) => setTwoFactorCode(e.target.value)}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      style={{
                        flex: 1,
                        padding: '12px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '16px',
                        letterSpacing: '4px',
                        textAlign: 'center'
                      }}
                    />
                    <button
                      onClick={verifyTwoFactor}
                      style={{
                        padding: '12px 24px',
                        borderRadius: '8px',
                        border: 'none',
                        background: '#3b82f6',
                        color: 'white',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Verify
                    </button>
                  </div>
                  <div>
                    <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>Backup codes (save these):</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                      {twoFactorData.backupCodes.map((code, i) => (
                        <span key={i} style={{
                          padding: '4px 8px',
                          background: 'white',
                          borderRadius: '4px',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          textAlign: 'center'
                        }}>
                          {code}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Active Sessions */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                Active Sessions
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sessions.map(session => (
                  <div
                    key={session.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px',
                      background: session.current ? '#f0fdf4' : '#f8fafc',
                      borderRadius: '8px',
                      border: session.current ? '1px solid #bbf7d0' : '1px solid #e2e8f0'
                    }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontWeight: '500', color: '#1e293b' }}>{session.device}</span>
                        {session.current && (
                          <span style={{
                            padding: '2px 8px',
                            background: '#10b981',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            Current
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '13px', color: '#64748b' }}>
                        {session.location} · {session.ip} · Last active: {new Date(session.lastActive).toLocaleString()}
                      </p>
                    </div>
                    {!session.current && (
                      <button
                        onClick={() => revokeSession(session.id)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          border: '1px solid #fecaca',
                          background: 'white',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* API Keys */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '4px' }}>
                    API Keys
                  </h3>
                  <p style={{ color: '#64748b', fontSize: '14px' }}>
                    Manage API keys for programmatic access
                  </p>
                </div>
                <button
                  onClick={() => setShowNewApiKey(true)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#3b82f6',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Create New Key
                </button>
              </div>

              {showNewApiKey && (
                <div style={{
                  marginBottom: '20px',
                  padding: '20px',
                  background: '#f8fafc',
                  borderRadius: '8px'
                }}>
                  {createdApiKey ? (
                    <div>
                      <p style={{ marginBottom: '12px', color: '#166534', fontWeight: '500' }}>
                        API key created successfully! Copy it now - it won't be shown again:
                      </p>
                      <div style={{
                        padding: '12px',
                        background: 'white',
                        borderRadius: '8px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        wordBreak: 'break-all',
                        marginBottom: '12px'
                      }}>
                        {createdApiKey}
                      </div>
                      <button
                        onClick={() => {
                          setShowNewApiKey(false)
                          setCreatedApiKey(null)
                        }}
                        style={{
                          padding: '10px 20px',
                          borderRadius: '8px',
                          border: 'none',
                          background: '#3b82f6',
                          color: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        Done
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>
                          Key Name
                        </label>
                        <input
                          type="text"
                          value={newApiKeyName}
                          onChange={(e) => setNewApiKeyName(e.target.value)}
                          placeholder="e.g., Production API Key"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '8px' }}>
                          Permissions
                        </label>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          {['read', 'query', 'export'].map(perm => (
                            <label key={perm} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={newApiKeyPermissions.includes(perm)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewApiKeyPermissions(prev => [...prev, perm])
                                  } else {
                                    setNewApiKeyPermissions(prev => prev.filter(p => p !== perm))
                                  }
                                }}
                              />
                              <span style={{ textTransform: 'capitalize' }}>{perm}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => setShowNewApiKey(false)}
                          style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: '1px solid #e2e8f0',
                            background: 'white',
                            color: '#374151',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={createApiKey}
                          disabled={!newApiKeyName}
                          style={{
                            padding: '10px 20px',
                            borderRadius: '8px',
                            border: 'none',
                            background: newApiKeyName ? '#3b82f6' : '#94a3b8',
                            color: 'white',
                            cursor: newApiKeyName ? 'pointer' : 'not-allowed'
                          }}
                        >
                          Create Key
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {apiKeys.map(key => (
                  <div
                    key={key.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '16px',
                      background: '#f8fafc',
                      borderRadius: '8px'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '500', color: '#1e293b', marginBottom: '4px' }}>
                        {key.name}
                      </div>
                      <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: '#64748b' }}>
                        <span style={{ fontFamily: 'monospace' }}>{key.keyPreview}</span>
                        <span>Created: {new Date(key.createdAt).toLocaleDateString()}</span>
                        <span>Last used: {new Date(key.lastUsed).toLocaleDateString()}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                        {key.permissions.map(perm => (
                          <span
                            key={perm}
                            style={{
                              padding: '2px 8px',
                              background: '#e0e7ff',
                              color: '#4f46e5',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: '500'
                            }}
                          >
                            {perm}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteApiKey(key.id)}
                      style={{
                        padding: '8px 16px',
                        borderRadius: '8px',
                        border: '1px solid #fecaca',
                        background: 'white',
                        color: '#ef4444',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && activityStats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {/* Stats Cards */}
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              borderRadius: '12px',
              padding: '24px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Total Queries</div>
              <div style={{ fontSize: '40px', fontWeight: '700' }}>{activityStats.totalQueries}</div>
              <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '8px' }}>AI-powered queries executed</div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '12px',
              padding: '24px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Reports Generated</div>
              <div style={{ fontSize: '40px', fontWeight: '700' }}>{activityStats.totalReports}</div>
              <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '8px' }}>Custom reports created</div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              borderRadius: '12px',
              padding: '24px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Data Exports</div>
              <div style={{ fontSize: '40px', fontWeight: '700' }}>{activityStats.totalExports}</div>
              <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '8px' }}>Files exported</div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '12px',
              padding: '24px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Markets Explored</div>
              <div style={{ fontSize: '40px', fontWeight: '700' }}>{activityStats.marketsViewed}</div>
              <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '8px' }}>Unique markets analyzed</div>
            </div>

            {/* Top Markets */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                Top Markets Viewed
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {activityStats.topMarkets.map((market, i) => (
                  <div
                    key={market}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}
                  >
                    <span style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      background: '#f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: '#64748b'
                    }}>
                      {i + 1}
                    </span>
                    <span style={{ fontWeight: '500', color: '#1e293b' }}>{market}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Top Features */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                Most Used Features
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {activityStats.topFeatures.map(feature => (
                  <span
                    key={feature}
                    style={{
                      padding: '8px 16px',
                      background: '#f1f5f9',
                      borderRadius: '20px',
                      fontSize: '14px',
                      color: '#374151',
                      fontWeight: '500'
                    }}
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>

            {/* Account Info */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              padding: '24px'
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>
                Account Statistics
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Total Logins</span>
                  <span style={{ fontWeight: '500', color: '#1e293b' }}>{activityStats.loginCount}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b' }}>Avg Session Duration</span>
                  <span style={{ fontWeight: '500', color: '#1e293b' }}>{activityStats.avgSessionDuration}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
